import {intervalInversionStaffConfig, intervalInversionStaffValue,
  intervalInversionFromStaffValue, intervalInversionStaffFeedback} from '../utils/intervalInversion.js';
import { harmonicMinorTriadStaffConfig } from "../utils/harmonicMinorTriads.js";
import TriadAnalysisRows from "./TriadAnalysisRows";
import { hasStaffKeyName } from "../utils/staffKeyName.js";
import { useState } from "react";
import QuestionLayout from "./QuestionLayout";
import KeySignatureInputWidget from "./KeySignatureInput.jsx";
import SymbolPalette from "./staff/SymbolPalette";
import ScaledSvg from "./ScaledSVG";
import { renderStaffLines, renderClef } from "./staff/staffRender";
import {
  snapYToPitch,
  pitchToY,
  pitchToStaffIndex,
  staffIndexToY,
  LINE_GAP
} from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs.jsx";
import { ledgerLineGeometry } from "./staff/ledgerLineGeometry.js";
import { arrangeChordAccidentals } from "./staff/chordAccidentalLayout.js";
import { givenNoteStemUp } from "./staff/givenNoteStem";

/* ───────────────────── CONSTANTS ───────────────────── */

const STAFF_TOP = 40;
const STAFF_HEIGHT = 100;
const SVG_HEIGHT = 250;

const LEFT_MARGIN = 100;
const RIGHT_MARGIN = 1;
const MEASURE_WIDTH = 140;
const SPLIT_WIDTH = 22;
const HALF_STEP_WIDTH = 50;

const SCALE_LABEL_X = LEFT_MARGIN;
const SCALE_LABEL_Y = STAFF_TOP + STAFF_HEIGHT; // vertically centered on staff

const NAME_BOX_WIDTH = 70;
const NAME_BOX_HEIGHT = 36;

const NAME_INPUT_GUTTER = NAME_BOX_WIDTH * 2 + 40; // tonic + dominant + spacing

/* ───────────────────── HELPERS ───────────────────── */
// function normalizePitchNameInput(input) {
//   if (!input) return "";

//   const s = input.trim();

//   // Match: letter + 0–2 accidentals
//   const m = s.match(/^([A-Ga-g])([#bB]{0,2})$/);
//   if (!m) return s; // allow free typing, grading will handle validity

//   const letter = m[1].toUpperCase();
//   const acc = m[2].replace(/B/g, "b"); // normalize flats to lowercase b

//   return letter + acc;
// }

function applyCase(s, normalizeCase = "upper") {
  if (normalizeCase === "lower") return s.toLowerCase();
  if (normalizeCase === "none") return s;
  return s.toUpperCase();
}

function normalizeTypedInput(input, normalizeCase = "upper") {
  return applyCase(input || "", normalizeCase);
}

function getKeySignaturePromptName(config = {}) {
  const raw =
    config.keySignaturePromptName ||
    config.requestedKeySignature ||
    config.scaleLabel;

  if (typeof raw !== "string" || !raw.trim()) return null;

  // A natural/harmonic/melodic minor scale still uses the key signature
  // of the underlying minor key, so name the key rather than the scale form.
  const keyName = raw
    .trim()
    .replace(/\b(?:natural|harmonic|melodic)\s+minor\b/i, "minor")
    .replace(/\s+/g, " ");

  if (/\bminor\b/i.test(keyName)) {
    return keyName.charAt(0).toLowerCase() + keyName.slice(1).replace(/\bminor\b/i, "minor");
  }
  return keyName.charAt(0).toUpperCase() + keyName.slice(1);
}

function normalizePitchNameInput(input) {
  if (!input) return "";

  const s = input
    .trim()
    .replace(/♭/g, "b")
    .replace(/♯/g, "#")
    .replace(/♮/g, "");

  const m = s.match(/^([A-Ga-g])([#b]{0,2})$/);
  if (!m) return s;

  return m[1].toUpperCase() + m[2];
}

function needsSecondOffset(pitchA, pitchB, clef) {
  if (!pitchA || !pitchB) return false;
  const a = pitchToStaffIndex(pitchA, clef);
  const b = pitchToStaffIndex(pitchB, clef);
  return Math.abs(a - b) === 1;
}

function secondOffsetDirection(pitch, otherPitch, clef) {
  if (!pitch || !otherPitch) return 0;

  const a = pitchToStaffIndex(pitch, clef);
  const b = pitchToStaffIndex(otherPitch, clef);

  if (Math.abs(a - b) == 0) return LINE_GAP+7;
  if (Math.abs(a - b) !== 1) return 0;

  // Use the shared chord displacement once; keep the printed note anchored.
  return a > b ? 13 : -13;
}

export function AccidentalPalette({ selected, onSelect, showInstruction = false }) {
  // const buttons = [
  //   { label: "♭", value: "b" },
  //   // { label: "♮", value: "♮" },
  //   { label: "♯", value: "#" },
  //   { label: "none", value: "" }
  // ];
  const buttons = [
    { label: "𝄫", value: "bb" },
    { label: "♭", value: "b" },
    { label: "♮", value: "n" },
    { label: "♯", value: "#" },
    { label: "𝄪", value: "##" },
    { label: "none", value: "" }
  ];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginTop: 0,
        marginBottom: 10,
        flexWrap: "nowrap"
      }}
    >
      <div style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
        {buttons.map(({ label, value }) => {
          const active = selected === value;

          return (
            <button
              key={label}
              type="button"
              onClick={() => onSelect(value)}
              style={{
                fontSize: 16,
                padding: "4px 8px",
                border: active ? "2px solid #1976d2" : "1px solid #aaa",
                background: active ? "#e3f2fd" : "#fff",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {showInstruction && (
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.2,
            color: "#444",
            minWidth: 0,
            maxWidth: 430
          }}
        >
          To add an accidental, click it in the menu before you place a note.
        </div>
      )}
    </div>
  );
}

function AccidentalButtons({ onSelect }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[
        { label: "♭", value: "♭" },
        { label: "♯", value: "♯" },
      ].map(({ label, value }) => (
        <button
          key={value}
          tabIndex={-1}
          style={{
            fontSize: 16,
            padding: "2px 6px",
            cursor: "pointer"
          }}
          onPointerDown={(e) => {
            e.preventDefault(); // prevent blur
            onSelect(value);
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function LedgerLines({ pitch, clef, symbol }) {
  return <g>{ledgerLineGeometry(pitch, clef, symbol).map((line, index) => (
    <line key={index} x1={line.x1} x2={line.x2} y1={line.y} y2={line.y}
      stroke="#000" strokeWidth={1} />
  ))}</g>;
}

function canToggleStem(entry) {
  return (
    entry &&
    entry.symbol.endsWith("_note") &&
    !entry.symbol.startsWith("whole")
  );
}

function accidentalFromPitch(pitch) {
  if (pitch.includes("##")) return "##";
  if (pitch.includes("bb")) return "bb";
  if (pitch.includes("#")) return "#";
  if (pitch.includes("b")) return "b";
  return null;
}

/* ───────────────────── COMPONENT ───────────────────── */

export default function StaffInputWidget(props) {
  if (props.config.intervalInversion) {
    return <StaffInputCore {...props}
      config={intervalInversionStaffConfig(props.config)}
      value={intervalInversionStaffValue(props.value, props.config)}
      onChange={next => props.onChange?.(intervalInversionFromStaffValue(next, props.value, props.config))}
      feedback={intervalInversionStaffFeedback(props.feedback, props.config)} />;
  }
  const config = props.config.harmonicMinorTriads ? harmonicMinorTriadStaffConfig(props.config) : props.config;
  return <StaffInputCore {...props} config={config} />;
}

function StaffInputCore({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const {
    clef,
    allowedSymbols,
    prompts,
    inputMode,
    measures,
    scaleLength,
    showTonic,
    staffInputPolicy = {}
  } = config;

  const promptByMeasure = {};
  (prompts || []).forEach((p) => {
    promptByMeasure[p.measure] = p.text;
  });
  if (config.grading?.triadConstruction) {
    Object.assign(promptByMeasure, config.grading.triadConstruction.qualities || {});
  }

  /* ───────────── STAFF INPUT POLICY ───────────── */

  const {
    allowNoteInput = true,
    allowTonicInput = true,
    allowAccidentalInput = true,
    allowHalfStepInput = true,
    allowTypedAccidentalButtons = false,
    accidentalInputMode = "context"
  } = staffInputPolicy;

  const canPlaceNote = (target) =>
    target === "tonic" ? allowTonicInput : allowNoteInput;

  const effectiveScaleLength =
    scaleLength ?? config.slotCount ?? measures ?? 0;

  const normalizedCase = config.normalizedCase ?? "upper";

  function isInputSlot(slot) {
    if (!Array.isArray(config.inputSlots)) return true;
    return config.inputSlots.includes(slot);
  }

  function isLockedSlot(slot) {
    if (!Array.isArray(config.inputSlots)) return false;
    return !config.inputSlots.includes(slot);
  }

  function shouldRenderBarlineAfterSlot(slot) {
    if (
      hasKeySignatureInput &&
      keySignatureInputPosition === "end" &&
      slot === keySigSlot - 1
    ) {
      return true;
    }

    if (config.renderBarlines === false) return false;

    if (config.slotsPerBar) {
      return slot % config.slotsPerBar === 0 && slot < effectiveScaleLength;
    }

    return slot < measures;
  }

  const promptPosition = config.promptPosition ?? "below";

  const PROMPT_Y_ABOVE = STAFF_TOP - 30;
  const PROMPT_Y_BELOW = STAFF_TOP + STAFF_HEIGHT + 22;

  function promptY() {
    return promptPosition === "above"
      ? PROMPT_Y_ABOVE
      : PROMPT_Y_BELOW;
  }

  // const effectiveScaleLength = scaleLength ?? measures ?? 0;
  const SLOT_WIDTH = config.slotWidth ?? MEASURE_WIDTH;
  const isScaleQuestion = !!scaleLength;

  const keySignatureInputPosition =
    config.keySignatureInputPosition ?? "end"; // "start" | "end"

  const hasKeySignatureInput =
    !!config.allowKeySignatureInput;

  const keySignaturePromptName = getKeySignaturePromptName(config);

  const keySigSlot =
    config.keySignatureSlot ?? effectiveScaleLength;

  const isFrontKeySignature =
    hasKeySignatureInput &&
    keySignatureInputPosition === "start";

  const drawnKeySigCount =
    value.keySignatureAccidentals?.[1]?.length ?? 0;

  const FRONT_KEY_SIG_WIDTH =
    isFrontKeySignature && drawnKeySigCount > 0
      ? config.frontKeySignatureWidth ?? drawnKeySigCount * 10
      : 0;

  const SLOT_START_X =
    LEFT_MARGIN + FRONT_KEY_SIG_WIDTH;

  const totalSlots = effectiveScaleLength + (showTonic ? 1 : 0);
  const staffWidth =
  SLOT_START_X + totalSlots * SLOT_WIDTH + RIGHT_MARGIN;
  const endBarlineX =
  SLOT_START_X + totalSlots * SLOT_WIDTH;

  const endBarline = config.endBarline ? config.endBarline : "double";

  // typeNames suffixes (e.g. "Major") need a little more room inside
  // foreignObject on mobile Safari. Keep the control visually centered by
  // splitting the extra width to both sides, and also extend the SVG canvas
  // so the last slot's suffix is not clipped by the SVG viewport itself.
  const TYPE_NAME_SUFFIX_EXTRA = config.suffix ? 20 : 0;

  const svgWidth =
    20 + staffWidth + TYPE_NAME_SUFFIX_EXTRA +
    (isScaleQuestion && staffInputPolicy.allowTonicNameInput
      ? NAME_INPUT_GUTTER
      : 0);

  const [chosenSymbol, setSelectedSymbol] = useState(null);
  const selectedSymbol = allowedSymbols?.length === 1
    ? allowedSymbols[0]
    : chosenSymbol;
  const [selectedAccidental, setSelectedAccidental] = useState("");
  const [hover, setHover] = useState(null);
  // which given note is selected for accidental editing
  //const [activeSlot, setActiveSlot] = useState(null);
  const [activeScaleSlot, setActiveScaleSlot] = useState(null);
  const [activeNoteSlot, setActiveNoteSlot] = useState(null);
  const [focusedTypedSlot, setFocusedTypedSlot] = useState(null);

  const [hoverHalfStep, setHoverHalfStep] = useState(null);

  const [activeTextField, setActiveTextField] = useState(null);

  const [showKeySigModal, setShowKeySigModal] = useState(false);
  const [draftKeySigAccidentals, setDraftKeySigAccidentals] = useState(
    value.keySignatureAccidentals || {}
  );
  // "typed:3" | "tonicName" | "dominantName" | null

  const isCompositeGrading = !!config.grading;

  const FRONT_KEY_SIG_X =
    config.frontKeySignatureX ?? LEFT_MARGIN;

  const keySigX =
    keySignatureInputPosition === "start"
      ? FRONT_KEY_SIG_X
      : SLOT_START_X + keySigSlot * SLOT_WIDTH + 18;

  function isKeySignatureSlot(slot) {
    return (
      hasKeySignatureInput &&
      keySignatureInputPosition === "end" &&
      slot === keySigSlot
    );
  }

  const scaleNoteInputSlots = isScaleQuestion && allowNoteInput && inputMode === "placeNotes"
    ? Array.from({ length: effectiveScaleLength }, (_, i) => i + 1)
        .filter(slot => !isLockedSlot(slot) && !isKeySignatureSlot(slot) && !config.givenNotes?.[slot])
    : [];
  const firstScaleNoteInputSlot = scaleNoteInputSlots[0];

  function slotFeedback(slot) {
    return feedback?.parts?.[slot];
  }

  function updateIntervalSelect(slot, field, selectedValue) {
    onChange({
      ...value,
      intervalSelections: {
        ...(value.intervalSelections || {}),
        [slot]: {
          ...(value.intervalSelections?.[slot] || {}),
          [field]: selectedValue
        }
      }
    });
  }

  function Slur({ a, b, stroke = "#000", opacity = 1 }) {
    const x1 =
      SLOT_START_X + (a - 1) * SLOT_WIDTH + SLOT_WIDTH / 2 + 10;
    const x2 =
      SLOT_START_X + (b - 1) * SLOT_WIDTH + SLOT_WIDTH / 2 + 10;

    const y = STAFF_TOP - 20;

    return (
      <path
        d={`M ${x1} ${y} Q ${(x1 + x2) / 2} ${y - 8} ${x2} ${y}`}
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        opacity={opacity}
        pointerEvents="none"
      />
    );
  }

  function getStaffPointerPosition(e) {
    const svg = e.currentTarget.ownerSVGElement;
    const ctm = svg?.getScreenCTM();

    if (!svg || !ctm) return null;

    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;

    const transformed = pt.matrixTransform(ctm.inverse());
    const y = transformed.y;

    return {
      y,
      pitch: snapYToPitch(y, clef, {
        allowLedgerLines: config.allowLedgerLines !== false
      })
    };
  }

  /* ───────────────────── CLICK HANDLER ───────────────────── */
  // function insertAccidental(value, accGlyph) {
  //   if (!value) return accGlyph;

  //   // Remove existing accidentals
  //   const base = value.replace(/[♭♯#bB♮]/g, "");

  //   return normalizePitchNameInput(base + accGlyph);
  // }
  function insertAccidental(value, accGlyph) {
    return value + accGlyph;
    const normalized = normalizePitchNameInput(value || "");

    const m = normalized.match(/^([A-G])([#b]{0,2})$/);
    if (!m) return normalized || accGlyph;

    const letter = m[1];
    return normalizePitchNameInput(letter + accGlyph);
  }

  function handleStaffClick(target, y) {
    if (mode !== "edit") return;
    if (config.grading?.scalePlacement && config.givenNotes?.[target]) return;
    
    // Scale questions: clicking a given note selects it for accidentals
    if (
      isScaleQuestion &&
      config.givenNotes?.[target] &&
      allowAccidentalInput
    ) {
      if (config.givenScaleAccidentalSlots?.some(slot => String(slot) === String(target))) return;
      setActiveScaleSlot(target);
      return;
    }

    if (!canPlaceNote(target)) return;

    const pitch = snapYToPitch(y, clef, {
      allowLedgerLines: config.allowLedgerLines !== false
    });

    if (config.lockGivenNotes && config.givenNotes?.[target]?.pitches?.some(
      given => sameStaffPosition(given, pitch, clef)
    )) return;

    const maxNotes = config.maxNotesPerSlotBySlot?.[target] ?? config.maxNotesPerSlot ?? 1;
    const currentEntries = getSlotEntries(value, target);

    /* TONIC SLOT */
    if (target === "tonic") {
      if (value.tonic) {
        const updated = { ...value };
        delete updated.tonic;
        onChange(updated);
        return;
      }

      onChange({
        ...value,
        tonic: {
          symbol: config.tonicSymbol || "whole_note",
          pitch
        }
      });
      return;
    }

    if (target !== "tonic" && isLockedSlot(target)) return;

    if (isKeySignatureSlot(target)) return;

    /* BLOCK GIVEN NOTES */
    if (config.givenNotes?.[target] && allowNoteInput==false) return;

    const requiresPalette =
      inputMode === "placeNotes" && allowedSymbols?.length > 0;

    if (requiresPalette && !selectedSymbol) return;

    // if (value.notes?.[target]) {
    //   const updated = {
    //     ...value,
    //     notes: { ...value.notes }
    //   };
    //   delete updated.notes[target];

    //   if (updated.noteAccidentals) {
    //     updated.noteAccidentals = { ...updated.noteAccidentals };
    //     delete updated.noteAccidentals[target];
    //   }

    //   onChange(updated);
    //   return;
    // }
    const existingIndex = currentEntries.findIndex(e =>
      sameStaffPosition(e.pitch, pitch, clef)
    );

    if (existingIndex >= 0) {
      const nextEntries = currentEntries.filter((_, i) => i !== existingIndex);

      let nextValue = setSlotEntries(value, target, nextEntries, maxNotes);

      if (nextValue.noteAccidentals?.[target] && maxNotes > 1) {
        const oldAccs = value.noteAccidentals?.[target] || {};
        const nextAccs = {};

        nextEntries.forEach((_, newIndex) => {
          const oldIndex = newIndex >= existingIndex ? newIndex + 1 : newIndex;
          if (oldAccs[oldIndex] !== undefined) {
            nextAccs[newIndex] = oldAccs[oldIndex];
          }
        });

        nextValue.noteAccidentals = {
          ...(value.noteAccidentals || {}),
          [target]: nextAccs
        };
      } else if (nextValue.noteAccidentals && !nextEntries.length) {
        nextValue.noteAccidentals = { ...nextValue.noteAccidentals };
        delete nextValue.noteAccidentals[target];
      }

      onChange(nextValue);
      return;
    }

    if (currentEntries.length >= maxNotes) return;

    const symbol =
      inputMode === "placeNotes"
        ? selectedSymbol
        : config.tonicSymbol;

    if (!symbol) return;

    const needsStem =
      symbol.endsWith("_note") &&
      !symbol.startsWith("whole");

    const newEntry = {
      symbol,
      pitch,
      stemUp: needsStem ? true : null
    };

    let nextValue = setSlotEntries(
      value,
      target,
      [...currentEntries, newEntry],
      maxNotes
    );

    if (
      allowAccidentalInput &&
      accidentalInputMode === "palette" &&
      selectedAccidental !== ""
    ) {
      const newIndex = currentEntries.length;

      if (maxNotes > 1) {
        nextValue.noteAccidentals = {
          ...(value.noteAccidentals || {}),
          [target]: {
            ...(value.noteAccidentals?.[target] || {}),
            [newIndex]: selectedAccidental
          }
        };
      } else {
        nextValue.noteAccidentals = {
          ...(value.noteAccidentals || {}),
          [target]: selectedAccidental
        };
      }
    }

    onChange(nextValue);
  }

  function toggleHalfStep(a, b) {
    if (!allowHalfStepInput) return;
    const key = `${a}-${b}`;
    const next = { ...(value.halfSteps || {}) };

    if (next[key]) delete next[key];
    else next[key] = true;

    onChange({
      ...value,
      halfSteps: next
    });
  }

  function DealBarlines(props) {
    if (props.double) {
      return (
        <>
          <rect
            x={props.x + SLOT_WIDTH-1.5}
            y={STAFF_TOP}
            width={1}
            height={40}
            fill="#000"
          />
          <rect
            x={props.x + SLOT_WIDTH+1.5}
            y={STAFF_TOP}
            width={1}
            height={40}
            fill="#000"
          />
        </>
      )  
    }
    return (
      <rect
        x={props.x + SLOT_WIDTH}
        y={STAFF_TOP}
        width={1}
        height={40}
        fill="#000"
      />
    )
  }

  /* ───────────────────── RENDER ───────────────────── */

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      legendImage={config.legendImage}
      mode={mode}
      feedback={feedback}
    >
      <div>
        {allowedSymbols?.length > 0 && (
          <div>
            <SymbolPalette
              allowedSymbols={allowedSymbols}
              selected={selectedSymbol}
              onSelect={setSelectedSymbol}
            />

            {allowAccidentalInput &&
              accidentalInputMode === "palette" &&
              inputMode === "placeNotes" && (
                <AccidentalPalette
                  selected={selectedAccidental}
                  onSelect={setSelectedAccidental}
                  showInstruction={mode === "edit"}
                />
              )}
          </div>
        )}

        {mode === "edit" && hasKeySignatureInput && (
          <div style={{ marginBottom: 8 }}>
            <button
              style={{ background: "lightgrey" }}
              onClick={() => {
                setDraftKeySigAccidentals(value.keySignatureAccidentals || {});
                setShowKeySigModal(true);
              }}
            >
              Draw Key Signature
            </button>
          </div>
        )}

        {showKeySigModal && (
          <>
            <div
              onClick={() => setShowKeySigModal(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 999
              }}
            />

            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                inset: "50px 0",
                background: "white",
                padding: 16,
                borderRadius: 8,
                maxWidth: 520,
                margin: "0 auto",
                maxHeight: "calc(100vh - 100px)",
                overflowY: "auto",
                overflowX: "hidden",
                zIndex: 1000,
                boxShadow: "0 12px 30px rgba(0,0,0,0.25)"
              }}
            >
              <KeySignatureInputWidget
                config={{
                  clef,
                  slots: 1,
                  prompt: keySignaturePromptName
                    ? `Draw ${keySignaturePromptName}`
                    : "Draw the key signature.",
                  number: `${config.number}-KS`,
                  points: config.keySignaturePoints ?? 1,
                  allowLedgerLines: false
                }}
                value={{ accidentals: draftKeySigAccidentals }}
                onChange={(v) =>
                  setDraftKeySigAccidentals(v.accidentals || {})
                }
                mode="edit"
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginTop: 12
                }}
              >
                <button onClick={() => setShowKeySigModal(false)}>
                  Cancel
                </button>

                <button
                  onClick={() => {
                    onChange({
                      ...value,
                      keySignatureAccidentals: draftKeySigAccidentals
                    });
                    setShowKeySigModal(false);
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </>
        )}

        <ScaledSvg
          logicalWidth={svgWidth}
          logicalHeight={config.harmonicMinorTriads ? 380 : SVG_HEIGHT}
          scale={config.staffDisplayScale}
        >
          {renderStaffLines(staffWidth)}
          {renderClef(clef)}
          {config.givenNoteRowLabel && (
            <text x={8} y={STAFF_TOP - 18} fontSize={14}
              fill="#000" pointerEvents="none">
              {config.givenNoteRowLabel}
            </text>
          )}
          {hasKeySignatureInput &&
          renderKeySignatureFromAccidentals(
            value.keySignatureAccidentals,
            clef,
            keySigX
          )}

          {/* ───────────── SCALE LABEL (LEFT OF STAFF) ───────────── */}
          {config.scaleLabel && (
            <text
              x={config.scaleLabelPosition === "above" ? 58 : SCALE_LABEL_X}
              y={config.scaleLabelPosition === "above" ? STAFF_TOP - 18 : SCALE_LABEL_Y}
              // textAnchor="end"
              fontSize="16"
              fontWeight="bold"
            >
              {config.scaleLabel}
            </text>
          )}

          {config.choiceRowLabel && Array.isArray(config.selectChoices) &&
            !config.intervalSelects && !config.answers?.choice && inputMode !== "typeNames" && (
              <text x={20} y={STAFF_TOP + STAFF_HEIGHT + 35}
                fontSize={14} fill="#000" pointerEvents="none">
                {config.choiceRowLabel}
              </text>
            )}

          {config.promptRowLabel && (
            <text
              x={config.promptRowLabelX ?? SLOT_START_X + SLOT_WIDTH / 2 - 57}
              y={promptY()}
              textAnchor="end"
              fontFamily={config.promptRowLabelFontFamily}
              fontSize={config.promptRowLabelSize ?? config.promptSize ?? 14}
              fontWeight="bold" fill="#000" pointerEvents="none">
              {config.promptRowLabel}
            </text>
          )}
          {/* ───────────── SCALE / MEASURE SLOTS ───────────── */}
          {(config.intervalRows || config.intervalSelects?.showRowLabels) && (
            <g fill="#000" fontSize={14} pointerEvents="none">
              <text x={20} y={STAFF_TOP + STAFF_HEIGHT + 35}>Type:</text>
              <text x={20} y={STAFF_TOP + STAFF_HEIGHT + 67}>Size:</text>
              {(Array.isArray(config.intervalRows) ? config.intervalRows : []).map(row => {
                const center = SLOT_START_X + (Number(row.slot) - 1 + Number(row.span ?? 1) / 2) * SLOT_WIDTH + 8;
                return <g key={row.slot} textAnchor="middle">
                  <text x={center} y={STAFF_TOP + STAFF_HEIGHT + 35}>{row.quality}</text>
                  <text x={center} y={STAFF_TOP + STAFF_HEIGHT + 67}>{row.size}</text>
                </g>;
              })}
            </g>
          )}
          {Array.from({ length: effectiveScaleLength }).map((_, i) => {
            const slot = i + 1;
            const x = SLOT_START_X + i * SLOT_WIDTH;
            const maxNotesPerSlot = config.maxNotesPerSlotBySlot?.[slot] ?? config.maxNotesPerSlot ?? 1;
            const entries = getSlotEntries(value, slot);
            const entry = entries[0];

            const givenPitches = config.givenNotes?.[slot]?.pitches ?? [];

            // Glyph noteheads extend rightward from their SVG origin. Align
            // the indicator with the visible head center, not that origin.
            const scaleInputSymbol = selectedSymbol || allowedSymbols?.[0] || "whole_note";
            const noteheadCenterOffset = scaleInputSymbol.includes("whole_note")
              ? 495 / 2 * 0.035
              : scaleInputSymbol.includes("half_note")
                ? 346 / 2 * 0.033
                : 329 / 2 * 0.035;
            const scaleStartIndicatorX =
              x + SLOT_WIDTH / 2 + (config.noteOffsetX ?? 0) + noteheadCenterOffset;

            // Preview only a note that this click could add. Hovering a locked
            // printed note or an existing entry must not reflow accidentals.
            const previewPitch = hover?.target === slot && mode === "edit" &&
              allowNoteInput && isInputSlot(slot) && !isLockedSlot(slot) &&
              !isKeySignatureSlot(slot) && entries.length < maxNotesPerSlot &&
              (selectedSymbol || inputMode !== "placeNotes") &&
              !entries.some(note => sameStaffPosition(note.pitch, hover.pitch, clef)) &&
              !(config.lockGivenNotes && givenPitches.some(pitch => sameStaffPosition(pitch, hover.pitch, clef)))
                ? hover.pitch : null;

            const layout = layoutChordNotes({
              slot,
              clef,
              value: config.givenScaleAccidentalSlots?.includes(slot)
                ? { ...value, accidentals: { ...value.accidentals, [slot]: undefined } }
                : value,
              givenPitches,
              splitGivenNotes: !!config.givenNotes?.[slot]?.split,
              entries,
              hoverPitch: previewPitch,
              givenSymbol: config.givenNotes?.[slot]?.symbol || "whole_note",
              hoverSymbol: selectedSymbol || allowedSymbols?.[0] || "whole_note",
              givenOffsetX: config.givenNotes?.[slot]?.offset || 0,
              entryOffsetX: config.noteOffsetX || 0,
              hoverAccidental: allowAccidentalInput && accidentalInputMode === "palette" ? selectedAccidental : null,
              offsetSingleEntrySecond:
                config.inputMode === "placeNotes" &&
                givenPitches.length === 1 &&
                maxNotesPerSlot === 1
            });
            //const fb = slotFeedback(slot);
            const fb = !isCompositeGrading ? slotFeedback(slot) : null;
            const hasSlotFeedback = typeof fb?.correct === "boolean";

            //second collisions
            // const givenPitches = config.givenNotes?.[slot]?.pitches ?? [];

            // const secondOffsetX =
            //   entry
            //     ? givenPitches.reduce(
            //         (acc, p) => acc || secondOffsetDirection(entry.pitch, p, clef),
            //         0
            //       )
            //     : 0;

            // const hoverSecondOffsetX =
            //   hover?.pitch
            //     ? givenPitches.reduce(
            //         (acc, p) => acc || secondOffsetDirection(hover.pitch, p, clef),
            //         0
            //       )
            //     : 0;

            const inputBorder =
            mode === "review" && !isCompositeGrading && hasSlotFeedback
              ? fb.correct
                ? "2px solid #2e7d32"
                : "2px solid #c62828"
              : "1px solid #aaa";

            return (
              <g key={slot}>
                {mode === "edit" && slot === firstScaleNoteInputSlot && entries.length === 0 && (
                  <g pointerEvents="none" aria-hidden="true">
                    <text
                      x={scaleStartIndicatorX}
                      y={STAFF_TOP - 25}
                      textAnchor="middle"
                      fontSize={13}
                      fontWeight="bold"
                      fill="#2563eb"
                    >
                      Start
                    </text>
                    <path
                      d={`M ${scaleStartIndicatorX - 4} ${STAFF_TOP - 20} l 4 5 l 4 -5`}
                      fill="none"
                      stroke="#2563eb"
                      strokeWidth={2}
                    />
                  </g>
                )}
                {/* KEY SIGNATURE */}
                {config.givenKeySignatures?.[slot] && (
                  <g>
                    {renderKeySignature(
                      config.givenKeySignatures[slot],
                      clef,
                      x + 18 * (slot>1)
                    )}
                  </g>
                )}

                {/* ───────────── HALF STEP SLURS ───────────── */}
                {allowHalfStepInput &&
                  Object.keys(value.halfSteps || {}).map((key) => {

                  const [a, b] = key.split("-").map(Number);

                  const x1 =
                    LEFT_MARGIN + (a - 1) * SLOT_WIDTH + SLOT_WIDTH / 2;
                  const x2 =
                    LEFT_MARGIN + (b - 1) * SLOT_WIDTH + SLOT_WIDTH / 2;

                  const y = STAFF_TOP - 10;

                  return <Slur key={key} a={a} b={b} />;
                })}

                {allowHalfStepInput && hoverHalfStep &&
                !value.halfSteps?.[`${hoverHalfStep.a}-${hoverHalfStep.b}`] && (
                  <Slur
                    a={hoverHalfStep.a}
                    b={hoverHalfStep.b}
                    stroke="#999"
                    opacity={0.6}
                  />
                )}

                {/* GIVEN NOTES */}
                {config.givenNotes?.[slot] && (
                  <g>
                    {config.givenNotes[slot].pitches.map((pitch, index) => {
                      const noteLayout = layout[`given-${index}`];
                      let splitX = 0;
                      if (config.givenNotes[slot].split) {
                        splitX =
                          index === 0 ? -SPLIT_WIDTH : SPLIT_WIDTH;
                      }

                      let offsetGX = 0;
                      if (config.givenNotes[slot].offset) {
                        offsetGX = config.givenNotes[slot].offset;
                      }

                      const isHighlight =
                      config.givenNotes[slot].highlight === pitch;
                      //console.log(activeNoteSlot);

                      return (
                        <g
                          key={pitch + index}
                          transform={`translate(
                            ${x + SLOT_WIDTH / 2 + splitX + offsetGX + (noteLayout?.noteOffsetX ?? 0)},
                            ${pitchToY(pitch, clef)}
                          )`}
                        >
                          {/* <Glyph
                            symbol={config.givenNotes[slot].symbol}
                            fill={activeSlot === slot ? "#1e90ff" : "#000"}
                            accidental={
                              value[slot] === "♮"
                                ? null
                                : value[slot] ?? accidentalFromPitch(pitch)
                            }
                          /> */}
                          <LedgerLines
                            pitch={pitch}
                            clef={clef}
                            x={x + SLOT_WIDTH / 2 + splitX}
                            symbol={config.givenNotes[slot].symbol}
                          />

                          <Glyph
                            symbol={config.givenNotes[slot].symbol}
                            stemUp={givenNoteStemUp(config.givenNotes[slot], index, clef)}
                            fill={activeScaleSlot === slot ? "#1e90ff" : "#000"}
                            accidental={noteLayout?.accidental}
                            accidentalOffsetX={noteLayout?.accidentalOffsetX}
                          />

                          {isHighlight && (
                            <circle
                              cx={9}
                              cy={0}
                              r={4}
                              fill="#000"
                              stroke="#000"
                              strokeWidth={2}
                              pointerEvents="none"
                            />
                          )}

                        </g>
                      );
                    })}
                  </g>
                )}

                {/* HIT AREA */}
                <rect
                  x={x}
                  y={STAFF_TOP - 30}
                  width={SLOT_WIDTH}
                  height={STAFF_HEIGHT}
                  fill="transparent"
                  // stroke={
                  //   mode === "review" &&
                  //   !isCompositeGrading &&
                  //   inputMode !== "typeNames"
                  //     ? fb?.correct
                  //       ? "#2e7d32"
                  //       : "#c62828"
                  //     : "none"
                  // }
                  stroke={
                    mode === "review" &&
                    !config.hideSlotFeedbackBoxes &&
                    hasSlotFeedback &&
                    !isCompositeGrading &&
                    inputMode !== "typeNames"
                      ? fb?.correct
                        ? "#2e7d32"
                        : "#c62828"
                      : "none"
                  }
                  // strokeWidth={
                  //   mode === "review" &&
                  //   !isCompositeGrading &&
                  //   inputMode !== "typeNames"
                  //     ? 2
                  //     : 0
                  // }
                  strokeWidth={
                    mode === "review" &&
                    !config.hideSlotFeedbackBoxes &&
                    hasSlotFeedback &&
                    !isCompositeGrading &&
                    inputMode !== "typeNames"
                      ? 2
                      : 0
                  }
                  style={{
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                    cursor: mode === "edit" ? "pointer" : "default"
                  }}
                  onPointerMove={(e) => {
                    if (mode !== "edit") return;
                    if (e.pointerType !== "mouse") return;
                    if (isKeySignatureSlot(slot)) return;
                    if (!canPlaceNote(slot)) return;
                    if (isLockedSlot(slot)) return;

                    const position = getStaffPointerPosition(e);
                    if (!position) return;

                    setHover({
                      target: slot,
                      ...position
                    });
                  }}
                  onPointerLeave={(e) => {
                    if (e.pointerType === "mouse") {
                      setHover(null);
                    }
                  }}
                  onPointerDown={(e) => {
                    if (mode !== "edit") return;
                    if (e.pointerType === "mouse" && e.button !== 0) return;

                    // Scale questions can lock note placement while still
                    // allowing students to tap the printed notes to edit
                    // accidentals. A scale note may share the numeric
                    // keySignatureSlot used to POSITION the trailing key
                    // signature (Level 7 Q11 uses slot 8 for both). Do not
                    // let that layout marker make the printed note inert.
                    const canEditGivenScaleNote =
                      isScaleQuestion &&
                      !!config.givenNotes?.[slot] &&
                      allowAccidentalInput;

                    if (isKeySignatureSlot(slot) && !canEditGivenScaleNote) return;
                    if (!canEditGivenScaleNote && !canPlaceNote(slot)) return;
                    if (isLockedSlot(slot) && !canEditGivenScaleNote) return;

                    e.preventDefault();

                    // A touch tap must not depend on synthetic hover state.
                    const position = getStaffPointerPosition(e);
                    if (!position) return;

                    setHover(null);
                    setActiveScaleSlot(null);
                    setActiveNoteSlot(null);
                    handleStaffClick(slot, position.y);
                  }}
                  onPointerCancel={() => setHover(null)}
                />

                {/* PLACED NOTE */}
                {entries.map((placedEntry, entryIndex) => {
                  const noteLayout = layout[`entry-${entryIndex}`];

                  return (
                    <g
                      key={`entry-${entryIndex}-${placedEntry.pitch}`}
                      transform={`translate(
                        ${x + SLOT_WIDTH / 2 + (config.noteOffsetX ?? 0) + (noteLayout?.noteOffsetX ?? 0)},
                        ${pitchToY(placedEntry.pitch, clef)}
                      )`}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        if (mode !== "edit") return;
                        e.stopPropagation();

                        const nextEntries = entries.filter(
                          (_, index) => index !== entryIndex
                        );

                        let updated = setSlotEntries(
                          value,
                          slot,
                          nextEntries,
                          maxNotesPerSlot
                        );

                        if (maxNotesPerSlot > 1) {
                          const oldAccidentals =
                            value.noteAccidentals?.[slot] || {};

                          const nextAccidentals = {};

                          nextEntries.forEach((_, newIndex) => {
                            const oldIndex =
                              newIndex >= entryIndex
                                ? newIndex + 1
                                : newIndex;

                            if (oldAccidentals[oldIndex] !== undefined) {
                              nextAccidentals[newIndex] =
                                oldAccidentals[oldIndex];
                            }
                          });

                          updated = {
                            ...updated,
                            noteAccidentals: {
                              ...(value.noteAccidentals || {})
                            }
                          };

                          if (Object.keys(nextAccidentals).length > 0) {
                            updated.noteAccidentals[slot] =
                              nextAccidentals;
                          } else {
                            delete updated.noteAccidentals[slot];
                          }
                        } else {
                          updated = {
                            ...updated,
                            noteAccidentals: {
                              ...(value.noteAccidentals || {})
                            }
                          };

                          delete updated.noteAccidentals[slot];
                        }

                        onChange(updated);
                      }}
                      onContextMenu={(e) => {
                        if (mode !== "edit") return;
                        if (!allowAccidentalInput) return;

                        e.preventDefault();
                        e.stopPropagation();

                        setActiveNoteSlot({ slot, entryIndex });
                      }}
                    >
                      <rect
                        x={0}
                        y={-5}
                        width={17}
                        height={10}
                        fill="transparent"
                      />

                      <LedgerLines
                        pitch={placedEntry.pitch}
                        clef={clef}
                        symbol={placedEntry.symbol}
                      />

                      <Glyph
                        symbol={placedEntry.symbol}
                        stemUp={placedEntry.stemUp}
                        fill={
                          mode === "review" && slotFeedback(slot)?.correct === false
                            ? "#c62828"
                            : activeNoteSlot?.slot === slot &&
                          activeNoteSlot?.entryIndex === entryIndex
                            ? "#1e90ff"
                            : "#000"
                        }
                        accidental={noteLayout?.accidental}
                        accidentalOffsetX={noteLayout?.accidentalOffsetX}
                      />
                    </g>
                  );
                })}

                {/* SLOT PROMPT */}
                {config.givenNoteMembers?.[slot] && (
                  <text
                    x={x + SLOT_WIDTH / 2 + 8 + (config.givenNotes?.[slot]?.offset || 0)}
                    y={STAFF_TOP - 18}
                    textAnchor="middle"
                    fontSize={14}
                    fill="#000"
                    pointerEvents="none"
                  >
                    {config.givenNoteMembers[slot]}
                  </text>
                )}
                {promptByMeasure[slot] && (
                  <text
                    x={x + SLOT_WIDTH / 2 + 8}
                    y={promptY()}
                    textAnchor="middle"
                    fontSize={config.promptSize ?? 14}
                    fontWeight={config.promptWeight ?? "normal"}
                    fill="#000"
                    pointerEvents="none"
                  >
                    {promptByMeasure[slot]}
                  </text>
                )}

                {/* OCCUPIED-SLOT LABEL INPUT
                    One box is rendered per slot whenever that slot contains
                    one or more student-placed notes. This is useful for
                    chord labels such as i and V. */}
                {config.showOccupiedSlotBoxes &&
                  entries.length > 0 &&
                  inputMode !== "typeNames" && (
                    <foreignObject
                      x={
                        x +
                        SLOT_WIDTH / 2 -
                        (config.occupiedSlotBoxWidth ?? 46) / 2 +
                        (config.occupiedSlotBoxOffsetX ?? 0)
                      }
                      y={
                        STAFF_TOP +
                        STAFF_HEIGHT +
                        (config.occupiedSlotBoxOffsetY ?? 12)
                      }
                      width={config.occupiedSlotBoxWidth ?? 46}
                      height={config.occupiedSlotBoxHeight ?? 38}
                    >
                      <div xmlns="http://www.w3.org/1999/xhtml">
                        <input
                          value={value.slotLabels?.[slot] || ""}
                          disabled={mode !== "edit"}
                          aria-label={`Label for slot ${slot}`}
                          onChange={(e) =>
                            onChange({
                              ...value,
                              slotLabels: {
                                ...(value.slotLabels || {}),
                                [slot]: normalizeTypedInput(
                                  e.target.value,
                                  config.slotLabelNormalizeCase ?? "none"
                                )
                              }
                            })
                          }
                          style={{
                            width: "100%",
                            height: config.occupiedSlotBoxHeight ?? 34,
                            boxSizing: "border-box",
                            fontFamily: "myFirstFont",
                            textAlign: "center",
                            fontSize: config.occupiedSlotBoxFontSize ?? 17,
                            borderRadius: 4,
                            border:
                              mode === "review"
                                ? (
                                    feedback?.parts?.slotLabels?.[slot]?.correct ??
                                    feedback?.parts?.labels?.[slot]?.correct
                                  )
                                  ? "2px solid #2e7d32"
                                  : "2px solid #c62828"
                                : "1px solid #aaa"
                          }}
                        />
                      </div>
                    </foreignObject>
                  )}

                {config.answerFormat === "triadRootAndQuality" && (
                  <foreignObject x={x + SLOT_WIDTH / 2 - 65}
                    y={STAFF_TOP + STAFF_HEIGHT} width={150} height={105}>
                    <div xmlns="http://www.w3.org/1999/xhtml"
                      style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ position: "relative" }}>
                        <input aria-label={`Triad ${slot} root`} type="text"
                          value={value.typed?.[slot] || ""} disabled={mode !== "edit"}
                          autoComplete="off" spellCheck={false}
                          onFocus={() => setActiveTextField(`root:${slot}`)}
                          onBlur={() => setActiveTextField(null)}
                          onChange={(event) => onChange({ ...value,
                            typed: { ...(value.typed || {}), [slot]: event.target.value } })}
                          style={{ width: 54, height: 34, boxSizing: "border-box",
                            textAlign: "center", fontSize: 18, borderRadius: 4,
                            background: "#fff", color: "#000",
                            border: mode === "review"
                              ? `2px solid ${feedback?.parts?.[slot]?.rootCorrect ? "#2e7d32" : "#c62828"}`
                              : "1px solid #aaa" }} />
                        {mode === "edit" && activeTextField === `root:${slot}` && (
                          <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4 }}>
                            <AccidentalButtons onSelect={(acc) => onChange({ ...value,
                              typed: { ...(value.typed || {}),
                                [slot]: (value.typed?.[slot] || "").replace(/[#b♯♭n♮]+$/, "") + acc } })} />
                          </div>
                        )}
                      </div>
                      <select aria-label={`Triad ${slot} type`}
                        value={value.choices?.[slot] || ""} disabled={mode !== "edit"}
                        onChange={(event) => onChange({ ...value,
                          choices: { ...(value.choices || {}), [slot]: event.target.value } })}
                        style={{ width: 78, height: 34, fontSize: 15, borderRadius: 4,
                          background: "#fff", color: "#000",
                          border: mode === "review"
                            ? `2px solid ${feedback?.parts?.[slot]?.qualityCorrect ? "#2e7d32" : "#c62828"}`
                            : "1px solid #aaa" }}>
                        <option value="">—</option>
                        {["MAJ", "min", "AUG", "dim"].map(choice => <option key={choice} value={choice}>{choice}</option>)}
                      </select>
                    </div>
                  </foreignObject>
                )}

                {/* TEXT INPUT */}
                {inputMode === "typeNames" && config.answerFormat !== "triadRootAndQuality" &&
                (config.givenNotes?.[slot] || config.givenKeySignatures?.[slot]) && (
                  <foreignObject
                    x={
                      x + SLOT_WIDTH / 2 - 15 -
                      (config.widebox ? 15 : 0) -
                      TYPE_NAME_SUFFIX_EXTRA / 2
                    }
                    y={STAFF_TOP + STAFF_HEIGHT}
                    width={
                      50 +
                      (config.suffix ? 50 : 0) +
                      (config.widebox ? 30 : 0) +
                      TYPE_NAME_SUFFIX_EXTRA
                    }
                    height={allowTypedAccidentalButtons ? 80 : 50}
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 4
                      }}
                    >
                      
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6
                        }}
                      >
                        {/* INPUT + ACCIDENTALS ANCHOR */}
                        <div style={{ position: "relative" }}>
                          <input
                            value={value.typed?.[slot] || ""}
                            disabled={mode !== "edit"}
                            onFocus={() => setActiveTextField(`typed:${slot}`)}
                            onBlur={() => setActiveTextField(null)}
                            onChange={(e) =>
                              onChange({
                                ...value,
                                typed: {
                                  ...(value.typed || {}),
                                  [slot]: normalizeTypedInput(e.target.value, normalizedCase)
                                }
                              })
                            }
                            style={{
                              width: config.widebox ? "72px" : "42px",
                              height: "42px",
                              textAlign: "center",
                              fontSize: "18px",
                              border:
                              mode === "review"
                                ? (feedback?.parts?.typed?.correct || feedback?.parts?.[slot]?.correct)
                                  ? "2px solid #2e7d32"
                                  : "2px solid #c62828"
                                : "1px solid #aaa",
                              borderRadius: "4px"
                            }}
                          />

                          {allowTypedAccidentalButtons &&
                            activeTextField === `typed:${slot}` &&
                            mode === "edit" && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  marginTop: 4,
                                  zIndex: 10
                                }}
                              >
                                <AccidentalButtons
                                  onSelect={(acc) =>
                                    onChange({
                                      ...value,
                                      typed: {
                                        ...(value.typed || {}),
                                        [slot]: insertAccidental(
                                          value.typed?.[slot] || "",
                                          acc
                                        )
                                      }
                                    })
                                  }
                                />
                              </div>
                            )}
                        </div>

                        {/* SUFFIX — NOT PART OF ANCHOR */}
                        {config.suffix && (
                          <span
                            style={{
                              fontSize: 16,
                              color: "#000",
                              fontWeight: "bold",
                              whiteSpace: "nowrap"
                            }}
                          >
                            {config.suffix}
                          </span>
                        )}
                      </div>

                                          </div>

                                        </foreignObject>
                                      )}

                                        {/* Selector
                                        {Array.isArray(config.selectChoices) &&
                                          !config.answers?.choice &&
                                          inputMode !== "typeNames" && (
                                            <foreignObject
                                              x={x + SLOT_WIDTH / 2 - 25}
                                              y={STAFF_TOP + STAFF_HEIGHT + 15}
                                              width={60}
                                              height={34}
                                            >
                                              <div xmlns="http://www.w3.org/1999/xhtml">
                                                <select
                                                  value={value.choices?.[slot] || ""}
                                                  disabled={mode !== "edit"}
                                                  onChange={(e) =>
                                                    onChange({
                                                      ...value,
                                                      choices: {
                                                        ...(value.choices || {}),
                                                        [slot]: e.target.value
                                                      }
                                                    })
                                                  }
                                                  style={{
                                                    width: "100%",
                                                    height: "30px",
                                                    fontSize: "14px",
                                                    textAlign: "center",
                                                    borderRadius: "4px",
                                                    border:
                                                      mode === "review"
                                                        ? feedback?.parts?.[slot]?.correct
                                                          ? "2px solid #2e7d32"
                                                          : "2px solid #c62828"
                                                        : "1px solid #aaa"
                                                  }}
                                                >
                                                  <option value="">—</option>
                                                  {config.selectChoices.map(choice => (
                                                    <option key={choice} value={choice}>
                                                      {choice}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            </foreignObject>
                                          )} */}
                                          {/* TWO-PART INTERVAL SELECTOR */}
{config.intervalSelects &&
  (!config.intervalSelects.slots || config.intervalSelects.slots.map(String).includes(String(slot))) &&
  inputMode !== "typeNames" && (
    <foreignObject
      x={x + SLOT_WIDTH / 2 - 55}
      y={STAFF_TOP + STAFF_HEIGHT + 15}
      width={120}
      height={70}
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "center"
        }}
      >
        <select
          value={value.intervalSelections?.[slot]?.quality || ""}
          disabled={mode !== "edit"}
          onChange={(e) =>
            updateIntervalSelect(slot, "quality", e.target.value)
          }
          style={{
            width: "95px",
            height: "28px",
            fontSize: "13px",
            borderRadius: "4px",
            border:
              mode === "review"
                ? (feedback?.parts?.[slot]?.qualityCorrect ?? feedback?.parts?.[slot]?.correct)
                  ? "2px solid #2e7d32"
                  : "2px solid #c62828"
                : "1px solid #aaa"
          }}
        >
          <option value="">type</option>
          {config.intervalSelects.qualities.map(choice => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>

        <select
          value={value.intervalSelections?.[slot]?.size || ""}
          disabled={mode !== "edit"}
          onChange={(e) =>
            updateIntervalSelect(slot, "size", e.target.value)
          }
          style={{
            width: "95px",
            height: "28px",
            fontSize: "13px",
            borderRadius: "4px",
            border:
              mode === "review"
                ? (feedback?.parts?.[slot]?.sizeCorrect ?? feedback?.parts?.[slot]?.correct)
                  ? "2px solid #2e7d32"
                  : "2px solid #c62828"
                : "1px solid #aaa"
          }}
        >
          <option value="">size</option>
          {config.intervalSelects.sizes.map(choice => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      </div>
    </foreignObject>
  )}

  {/* PER-SLOT BUTTON SELECTOR */}
{Array.isArray(config.selectChoicesBySlot?.[slot]) &&
  inputMode !== "typeNames" && (
    <foreignObject
      x={x + SLOT_WIDTH / 2 - 85 + (config.offshitX?config.offshitX:0)}
      y={STAFF_TOP + STAFF_HEIGHT + 15}
      width={180}
      height={40}
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 6
        }}
      >
        {config.selectChoicesBySlot[slot].map(choice => {
          const isSelected = value.choices?.[slot] === choice;

          const border =
            mode === "review" && isSelected
              ? feedback?.parts?.[slot]?.correct
                ? "2px solid #2e7d32"
                : "2px solid #c62828"
              : isSelected
              ? "2px solid #1976d2"
              : "1px solid #aaa";

          return (
            <button
              key={choice}
              type="button"
              disabled={mode !== "edit"}
              onClick={() =>
                onChange({
                  ...value,
                  choices: {
                    ...(value.choices || {}),
                    [slot]: choice
                  }
                })
              }
              style={{
                padding: "5px 8px",
                fontSize: 13,
                borderRadius: 4,
                background: isSelected ? "#e3f2fd" : "#fff",
                border,
                cursor: mode === "edit" ? "pointer" : "default"
              }}
            >
              {choice}
            </button>
          );
        })}
      </div>
    </foreignObject>
)}

{/* OLD SINGLE SELECTOR */}
{!config.intervalSelects &&
  !Array.isArray(config.selectChoicesBySlot?.[slot]) &&
  Array.isArray(config.selectChoices) &&
  !config.answers?.choice &&
  inputMode !== "typeNames" && (
    <foreignObject
      x={x + SLOT_WIDTH / 2 - 25}
      y={STAFF_TOP + STAFF_HEIGHT + 15}
      width={60}
      height={34}
    >
      <div xmlns="http://www.w3.org/1999/xhtml">
        <select
          value={value.choices?.[slot] || ""}
          disabled={mode !== "edit"}
          onChange={(e) =>
            onChange({
              ...value,
              choices: {
                ...(value.choices || {}),
                [slot]: e.target.value
              }
            })
          }
          style={{
            width: "100%",
            height: "30px",
            fontSize: "14px",
            textAlign: "center",
            borderRadius: "4px",
            border:
              mode === "review"
                ? (feedback?.parts?.[slot]?.qualityCorrect ?? feedback?.parts?.[slot]?.correct)
                  ? "2px solid #2e7d32"
                  : "2px solid #c62828"
                : "1px solid #aaa"
          }}
        >
          <option value="">—</option>
          {config.selectChoices.map(choice => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      </div>
    </foreignObject>
  )}




                                      {/* STEM TOGGLE BUTTON */}
                                      {inputMode === "placeNotes" &&
                                        canToggleStem(entry) && (
                                          <foreignObject
                                            x={x + SLOT_WIDTH / 2 - 40}
                                            y={STAFF_TOP + STAFF_HEIGHT + 38}
                                            width={80}
                                            height={36}
                                          >
                                            <button
                                              style={{
                                                width: "80px",
                                                fontSize: "11px",
                                                cursor: "pointer"
                                              }}
                                              onClick={(e) => {
                                                if (mode === "review") return;
                                                e.stopPropagation();

                                                const nextStem =
                                                  entry.stemUp === true ? false : true;

                                                onChange({
                                                  ...value,
                                                  notes: {
                                                    ...(value.notes || {}),
                                                    [slot]: {
                                                      ...entry,
                                                      stemUp: nextStem
                                                    }
                                                  }
                                                });
                                              }}
                                            >
                                              {entry.stemUp ? "stem up" : "stem down"}
                                            </button>
                                          </foreignObject>
                                        )}

                                      {/* BARLINES */}
                                      {/* {!isScaleQuestion && slot<config.measures &&
                                        config.renderBarlines !== false && <DealBarlines x={x} double={config.double}/>} */}

                                      {(!isScaleQuestion || config.renderBarlines === true) &&
                                      shouldRenderBarlineAfterSlot(slot) && (
                                        <DealBarlines x={x} double={config.double} />
                                      )}

                                      {isScaleQuestion &&
                                        showTonic &&
                                        slot === effectiveScaleLength && (
                                          <rect
                                            x={x + SLOT_WIDTH}
                                            y={STAFF_TOP}
                                            width={1}
                                            height={40}
                                            fill="#000"
                                          />
                                        )}

                                      {/* HOVER GHOST */}
                                      {/* {hover?.target === slot &&
                                        allowNoteInput &&
                                        isInputSlot(slot) &&
                                        entries.length < maxNotesPerSlot &&
                                        (selectedSymbol || inputMode !== "placeNotes") && (
                                          <g
                                            transform={`translate(
                                              ${x + SLOT_WIDTH / 2 + hoverSecondOffsetX},
                                              ${pitchToY(hover.pitch, clef)}
                                            )`}
                                            opacity={0.5}
                                            pointerEvents="none"
                                          >
                                            <LedgerLines
                                              pitch={hover.pitch}
                                              clef={clef}
                                              x={x + SLOT_WIDTH / 2}
                                              symbol={selectedSymbol || "whole_note"}
                                            />
                                            <Glyph
                                              symbol={selectedSymbol || "whole_note"}
                                              fill="#999"
                                            />
                                          </g>
                                        )} */}
                                        {hover?.target === slot &&
                                        layout.hover &&
                                        allowNoteInput &&
                                        isInputSlot(slot) &&
                                        entries.length < maxNotesPerSlot &&
                                        (selectedSymbol || inputMode !== "placeNotes") &&
                                        (() => {
                                          const noteLayout = layout.hover;

                                          return (
                                            <g
                                              transform={`translate(
                                                ${x + SLOT_WIDTH / 2 + (config.noteOffsetX ?? 0) + (noteLayout?.noteOffsetX ?? 0)},
                                                ${pitchToY(hover.pitch, clef)}
                                              )`}
                                              opacity={0.5}
                                              pointerEvents="none"
                                            >
                                              <LedgerLines
                                                pitch={hover.pitch}
                                                clef={clef}
                                                symbol={selectedSymbol || "whole_note"}
                                              />
                                              <Glyph
                                                symbol={selectedSymbol || "whole_note"}
                                                fill="#999"
                                                accidental={noteLayout?.accidental}
                                                accidentalOffsetX={noteLayout?.accidentalOffsetX}
                                              />
                                            </g>
                                          );
                                        })()}
                                    </g>
                                  );
                                })}

                                {/* ───────────── TONIC SLOT ───────────── */}
                                {showTonic && (() => {
                                  const x =
                                    -10 + SLOT_START_X + effectiveScaleLength * SLOT_WIDTH;
                                  const centerX = x + SLOT_WIDTH / 2;

                                  return (
                                    <>
                                      <rect
                                        x={x}
                                        y={STAFF_TOP - 20}
                                        width={SLOT_WIDTH}
                                        height={STAFF_HEIGHT}
                                        fill="transparent"
                                        style={{
                                          touchAction: "manipulation",
                                          WebkitTapHighlightColor: "transparent",
                                          cursor: mode === "edit" ? "pointer" : "default"
                                        }}
                                        onPointerMove={(e) => {
                                          if (mode !== "edit") return;
                                          if (e.pointerType !== "mouse") return;
                                          if (!canPlaceNote("tonic")) return;
                                          if (
                                            hasKeySignatureInput &&
                                            keySignatureInputPosition === "end"
                                          ) return;

                                          const position = getStaffPointerPosition(e);
                                          if (!position) return;

                                          setHover({
                                            target: "tonic",
                                            ...position
                                          });
                                        }}
                                        onPointerLeave={(e) => {
                                          if (e.pointerType === "mouse") {
                                            setHover(null);
                                          }
                                        }}
                                        onPointerDown={(e) => {
                                          if (mode !== "edit") return;
                                          if (e.pointerType === "mouse" && e.button !== 0) return;
                                          if (!canPlaceNote("tonic")) return;
                                          if (
                                            hasKeySignatureInput &&
                                            keySignatureInputPosition === "end"
                                          ) return;

                                          e.preventDefault();

                                          const position = getStaffPointerPosition(e);
                                          if (!position) return;

                                          setHover(null);
                                          handleStaffClick("tonic", position.y);
                                        }}
                                        onPointerCancel={() => setHover(null)}
                                      />

                                      {hover?.target === "tonic" &&
                                        allowTonicInput &&
                                        !value.tonic && (
                                          <g
                                            transform={`translate(
                                              ${centerX},
                                              ${pitchToY(hover.pitch, clef)}
                                            )`}
                                            opacity={0.5}
                                            pointerEvents="none"
                                          >
                                            <LedgerLines
                                              pitch={hover.pitch}
                                              clef={clef}
                                              x={x + SLOT_WIDTH / 2}
                                              symbol={config.tonicSymbol || "whole_note"}
                                            />
                                            <Glyph
                                              symbol={config.tonicSymbol || "whole_note"}
                                              fill="#999"
                                            />
                                          </g>
                                        )}

                                      {value.tonic && (
                                        <g
                                          transform={`translate(
                                            ${centerX},
                                            ${pitchToY(value.tonic.pitch, clef)}
                                          )`}
                                        >
                                          <LedgerLines
                                            pitch={value.tonic.pitch}
                                            clef={clef}
                                            x={x + SLOT_WIDTH / 2}
                                            symbol="whole_note"
                                          />
                                          <Glyph symbol="whole_note" />
                                        </g>
                                      )}

                                      {config.tonicLabel && (
                                        <text
                                          x={centerX}
                                          y={promptY()-25}
                                          textAnchor="middle"
                                          fontSize={config.tonicLabelSize ?? 14}
                                          fontWeight="bold"
                                          fill="#000"
                                          pointerEvents="none"
                                        >
                                          {config.tonicLabel}
                                        </text>
                                      )}
                                    </>
                                  );
                                })()}

                                {/* ───────────── TONIC / DOMINANT NAME INPUTS ───────────── */}
                                {isScaleQuestion && staffInputPolicy.allowTonicNameInput && (
                                  (() => {
                                    const baseX =
                                      SLOT_START_X + (effectiveScaleLength + (showTonic ? 1 : 0)) * SLOT_WIDTH + 10;

                                    const labelY = STAFF_TOP + 8;
                                    const inputY = labelY + 16;

                                    const inputStyle = {
                                      width: NAME_BOX_WIDTH,
                                      height: NAME_BOX_HEIGHT,
                                      textAlign: "center",
                                      fontSize: "18px",
                                      borderRadius: "4px",
                                      boxSizing: "border-box"
                                    };

                                    return (
                                      <>
                                        {/* TONIC NAME */}
                                        <g>
                                          <text
                                            x={baseX + NAME_BOX_WIDTH / 2}
                                            y={labelY}
                                            textAnchor="middle"
                                            fontSize="12"
                                          >
                                            Tonic
                                          </text>

                                          <foreignObject
                                            x={baseX}
                                            y={inputY}
                                            width={NAME_BOX_WIDTH}
                                            height={NAME_BOX_HEIGHT + (allowTypedAccidentalButtons ? 50 : 0)}
                                          >
                                            <div
                                              xmlns="http://www.w3.org/1999/xhtml"
                                              style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 4
                                              }}
                                            >
                                              <input
                                                value={value.tonicName || ""}
                                                disabled={mode !== "edit"}
                                                onFocus={() => setActiveTextField("tonicName")}
                                                onBlur={() => setActiveTextField(null)}
                                                onChange={(e) =>
                                                  onChange({
                                                    ...value,
                                                    tonicName: e.target.value.toUpperCase()
                                                  })
                                                }
                                                style={{
                                                  ...inputStyle,
                                                  border:
                              mode === "review"
                                ? feedback?.parts?.tonicName?.correct
                                  ? "2px solid #2e7d32"
                                  : "2px solid #c62828"
                                : "1px solid #aaa"
                          }}
                        />

                        {activeTextField === "tonicName" &&
                          mode === "edit" &&
                          allowAccidentalInput && (
                            <AccidentalButtons
                              onSelect={(acc) =>
                                onChange({
                                  ...value,
                                  tonicName: insertAccidental(value.tonicName || "", acc)
                                })
                              }
                            />
                          )}
                      </div>

                    </foreignObject>
                  </g>

                  {/* DOMINANT NAME */}
                  <g transform={`translate(${NAME_BOX_WIDTH + 14}, 0)`}>
                    <text
                      x={baseX + NAME_BOX_WIDTH / 2}
                      y={labelY}
                      textAnchor="middle"
                      fontSize="12"
                    >
                      Dominant
                    </text>

                    <foreignObject
                      x={baseX}
                      y={inputY}
                      width={NAME_BOX_WIDTH}
                      height={NAME_BOX_HEIGHT + (allowTypedAccidentalButtons ? 50 : 0)}
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 4
                        }}
                      >
                        <input
                          value={value.dominantName || ""}
                          disabled={mode !== "edit"}
                          onFocus={() => setActiveTextField("dominantName")}
                          onBlur={() => setActiveTextField(null)}
                          onChange={(e) =>
                            onChange({
                              ...value,
                              dominantName: e.target.value.toUpperCase()
                            })
                          }
                          style={{
                            ...inputStyle,
                            border:
                              mode === "review"
                                ? feedback?.parts?.dominantName?.correct
                                  ? "2px solid #2e7d32"
                                  : "2px solid #c62828"
                                : "1px solid #aaa"
                          }}
                        />

                        {activeTextField === "dominantName" &&
                          mode === "edit" &&
                          allowAccidentalInput && (
                            <AccidentalButtons
                              onSelect={(acc) =>
                                onChange({
                                  ...value,
                                  dominantName: insertAccidental(value.dominantName || "", acc)
                                })
                              }
                            />
                          )}
                      </div>
                    </foreignObject>
                  </g>
                </>
              );
            })()
          )}


          {/* ───────────── HALF STEP HIT AREAS (TOP LAYER) ───────────── */}
          {allowHalfStepInput && isScaleQuestion &&
            Array.from({ length: effectiveScaleLength - 1 }).map((_, i) => {
              const a = i + 1;
              const b = a + 1;

              const x =
                SLOT_START_X + a * SLOT_WIDTH - HALF_STEP_WIDTH / 2;
              
              const isInteractive = mode === "edit";
              return (
                <rect
                  key={`half-hit-${a}`}
                  x={x + 9}
                  y={STAFF_TOP - 30}
                  width={HALF_STEP_WIDTH}
                  height={STAFF_HEIGHT + 20}
                  fill="transparent"
                  pointerEvents={isInteractive ? "auto" : "none"}
                  cursor={isInteractive ? "pointer" : "default"}
                  style={{
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                  onPointerEnter={
                    isInteractive
                      ? (e) => {
                          if (e.pointerType === "mouse") {
                            setHoverHalfStep({ a, b });
                          }
                        }
                      : undefined
                  }
                  onPointerLeave={
                    isInteractive
                      ? (e) => {
                          if (e.pointerType === "mouse") {
                            setHoverHalfStep(null);
                          }
                        }
                      : undefined
                  }
                  onPointerDown={
                    isInteractive
                      ? (e) => {
                          if (e.pointerType === "mouse" && e.button !== 0) return;

                          e.preventDefault();
                          e.stopPropagation();
                          setHoverHalfStep(null);
                          toggleHalfStep(a, b);
                        }
                      : undefined
                  }
                  onPointerCancel={() => setHoverHalfStep(null)}
                />
              );
            })}

            {/* ───────────── END BARLINE ───────────── */}
            {endBarline === "double" && (
              <>
                <rect
                  x={endBarlineX - 5}
                  y={STAFF_TOP}
                  width={1}
                  height={40}
                  fill="#000"
                />
                <rect
                  x={endBarlineX - 2}
                  y={STAFF_TOP}
                  width={3}
                  height={40}
                  fill="#000"
                />
              </>
            )}

          {config.harmonicMinorTriads && <TriadAnalysisRows startX={SLOT_START_X} slotWidth={SLOT_WIDTH}
            value={value} onChange={onChange} mode={mode} feedback={feedback} />}
        </ScaledSvg>

        {hasStaffKeyName(config) && (
          <div style={{marginLeft:50, marginBottom:16, color:"#111"}}>
            <label style={{display:"inline-flex", alignItems:"center", gap:8}}>
              {config.keyNameInput?.label || ""}
              <input type="text" aria-label="Key name" value={value.keyName || ""}
                disabled={mode !== "edit"} autoComplete="off" spellCheck={false}
                onFocus={() => setActiveTextField("keyName")} onBlur={() => setActiveTextField(null)}
                onChange={event => onChange({...value, keyName:event.target.value})}
                style={{width:80, height:34, textAlign:"center", fontSize:18, color:"#111", background:"#fff",
                  borderRadius:4, border:mode === "review" && feedback?.parts?.keyName
                    ? `2px solid ${feedback.parts.keyName.correct ? "#2e7d32" : "#c62828"}` : "1px solid #888"}}/>
              {config.keyNameInput?.suffix ?? "Major"}
            </label>
            {mode === "edit" && activeTextField === "keyName" && <AccidentalButtons
              onSelect={acc => onChange({...value, keyName:(value.keyName || "").replace(/[#b♯♭n♮]+$/, "") + acc})}/>}
            {mode === "review" && feedback?.parts?.keyName?.correct === false && (
              <small style={{display:"block", color:"#b91c1c"}}>Expected: {feedback.parts.keyName.expected || "(answer key missing)"}</small>
            )}
          </div>
        )}

        {config.scaleNameInput && (
          <div style={{ marginLeft: 50, marginBottom: 16, color: "#000" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="text" aria-label="Minor scale tonic"
              value={value.scaleName || ""} disabled={mode !== "edit"}
              onFocus={() => setActiveTextField("scaleName")}
              onBlur={() => setActiveTextField(null)}
              autoComplete="off" spellCheck={false}
              onChange={(event) => onChange({ ...value, scaleName: event.target.value })}
              style={{ width: 64, height: 34, textAlign: "center", fontSize: 18,
                borderRadius: 4, color: "#000", background: "#fff",
                border: mode === "review"
                  ? `2px solid ${feedback?.parts?.scaleName?.correct ? "#2e7d32" : "#c62828"}`
                  : "1px solid #aaa" }} />
            <span>{config.scaleNameInput.suffix}</span>
          </label>
          {mode === "edit" && activeTextField === "scaleName" && <AccidentalButtons
            onSelect={acc => onChange({...value, scaleName:(value.scaleName || "").replace(/[#b♯♭n♮]+$/, "") + acc})}/>}
          </div>
        )}

        {/* ───────────── FUNCTIONAL DEGREE SELECTOR ───────────── */}
        {Array.isArray(config.selectChoices) && config.answers?.choice && (
          <div style={{ marginTop: 0, marginBottom: 10, left: 50, position: "relative" }}>
            {config.selectChoices.map((choice) => {
              const isSelected = value.choice === choice;

              const border =
                mode === "review" && isSelected
                  ? feedback?.parts?.choice?.correct
                    ? "2px solid #2e7d32"
                    : "2px solid #c62828"
                  : isSelected
                  ? "2px solid #1976d2"
                  : "1px solid #aaa";

              return (
                <button
                  key={choice}
                  disabled={mode !== "edit"}
                  onClick={() =>
                    onChange({
                      ...value,
                      choice
                    })
                  }
                  style={{
                    marginRight: 8,
                    padding: "6px 14px",
                    fontSize: 16,
                    cursor: mode === "edit" ? "pointer" : "default",
                    background: isSelected ? "#e3f2fd" : "#fff",
                    border,
                    borderRadius: 4
                  }}
                >
                  {choice}
                </button>
              );
            })}
          </div>
        )}

        {activeNoteSlot && mode === "edit" && allowAccidentalInput && (
          <div
            style={{
              marginTop: -50,
              position: "relative",
              zIndex: 2
            }}
          >
            {[
              { key: "flat", label: "♭", value: "b" },
              { key: "sharp", label: "♯", value: "#" },
              { key: "natural", label: "clear", value: "" }
            ].map(({ key, label, value: accValue }) => {
              const applyAccidental = () => {
                if (typeof activeNoteSlot === "object") {
                  const { slot, entryIndex } = activeNoteSlot;
                  const maxNotes = config.maxNotesPerSlotBySlot?.[slot] ?? config.maxNotesPerSlot ?? 1;

                  if (maxNotes > 1) {
                    onChange({
                      ...value,
                      noteAccidentals: {
                        ...(value.noteAccidentals || {}),
                        [slot]: {
                          ...(value.noteAccidentals?.[slot] || {}),
                          [entryIndex]: accValue
                        }
                      }
                    });
                  } else {
                    onChange({
                      ...value,
                      noteAccidentals: {
                        ...(value.noteAccidentals || {}),
                        [slot]: accValue
                      }
                    });
                  }
                }

                setActiveNoteSlot(null);
              };

              return (
                <button
                  key={key}
                  type="button"
                  style={{
                    marginRight: 6,
                    fontSize: 18,
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                  onPointerDown={(e) => {
                    // On phones/tablets the staff sits in a horizontal scroller.
                    // Waiting for the synthesized click can be cancelled by that
                    // scroller, so commit the choice immediately on touch/pen.
                    if (e.pointerType === "mouse") return;
                    e.preventDefault();
                    e.stopPropagation();
                    applyAccidental();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyAccidental();
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}


        {isScaleQuestion &&
          allowAccidentalInput &&
          activeScaleSlot &&
          mode === "edit" && (
          <div
            style={{
              marginTop: -50,
              position: "relative",
              zIndex: 2
            }}
          >
            {[
              { key: "flat", label: "♭", value: "b" },
              { key: "sharp", label: "♯", value: "#" },
              ...(config.editorScaleAutomatic ? [
                { key: "double-flat", label: "𝄫", value: "bb" },
                { key: "double-sharp", label: "𝄪", value: "##" }
              ] : []),
              { key: "clear", label: "clear", value: "" }
            ].map(({ key, label, value: accValue }) => {
              const applyAccidental = () => {
                onChange({
                  ...value,
                  accidentals: {
                    ...(value.accidentals || {}),
                    [activeScaleSlot]: accValue
                  }
                });
                setActiveScaleSlot(null);
              };

              return (
                <button
                  key={key}
                  type="button"
                  style={{
                    marginRight: 6,
                    fontSize: 18,
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                  onPointerDown={(e) => {
                    // Mobile browsers can cancel the later click while this
                    // widget is inside the horizontally scrollable staff.
                    if (e.pointerType === "mouse") return;
                    e.preventDefault();
                    e.stopPropagation();
                    applyAccidental();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyAccidental();
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}


      </div>
    </QuestionLayout>
  );
}

/* ───────────────────── KEY SIGNATURE HELPERS ───────────────────── */

// Treble clef anchor pitches
const TREBLE_SHARP_Y = ["F#5","C#5","G#5","D#5","A#4","E#5","B#4"];
const TREBLE_FLAT_Y  = ["Bb4","Eb5","Ab4","Db5","Gb4","Cb5","Fb4"];

// Bass clef anchor pitches
const BASS_SHARP_Y = ["F#3","C#3","G#3","D#3","A#2","E#3","B#2"];
const BASS_FLAT_Y  = ["Bb2","Eb3","Ab2","Db3","Gb2","Cb3","Fb2"];

function getKeySignaturePitches(type, clef) {
  if (clef === "bass") {
    return type === "sharps" ? BASS_SHARP_Y : BASS_FLAT_Y;
  }
  // default to treble
  return type === "sharps" ? TREBLE_SHARP_Y : TREBLE_FLAT_Y;
}

function renderKeySignature({ type, count }, clef, baseX) {
  const pitches = getKeySignaturePitches(type, clef);

  return pitches.slice(0, count).map((pitch, i) => {
    const accidental = pitch.includes("#") ? "#" : "b";

    return (
      <g
        key={`${pitch}-${i}`}
        transform={`translate(${baseX + i * 10}, ${pitchToY(pitch, clef)})`}
      >
        <Glyph
          symbol="quarter_note"
          accidental={accidental}
          accidentalOnly
        />
      </g>
    );
  });
}

function renderKeySignatureFromAccidentals(accidentals, clef, baseX) {
  const slot = accidentals?.[1];
  if (!Array.isArray(slot)) return null;

  return slot.map((a, i) => (
    <g
      key={`ks-${i}-${a.pitch}-${a.accidental}`}
      transform={`translate(
        ${baseX + i * 10},
        ${pitchToY(a.pitch, clef)}
      )`}
    >
      <Glyph
        symbol="quarter_note"
        accidental={a.accidental}
        accidentalOnly
      />
    </g>
  ));
}

function getSlotEntries(value, slot) {
  const raw = value.notes?.[slot];
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
}

function setSlotEntries(value, slot, entries, maxNotesPerSlot = 1) {
  const nextNotes = { ...(value.notes || {}) };

  if (!entries.length) {
    delete nextNotes[slot];
  } else if (maxNotesPerSlot > 1) {
    nextNotes[slot] = entries;
  } else {
    nextNotes[slot] = entries[0];
  }

  return {
    ...value,
    notes: nextNotes
  };
}

// function getNoteAccidental(value, slot, entryIndex, pitch) {
//   const raw = value.noteAccidentals?.[slot];

//   if (typeof raw === "string") {
//     if (raw === "♮" || raw === "") return null;
//     return raw;
//   }

//   const acc = raw?.[entryIndex];

//   if (acc === "♮" || acc === "") return null;
//   if (acc !== undefined) return acc;

//   return accidentalFromPitch(pitch);
// }
function getNoteAccidental(value, slot, entryIndex, pitch) {
  const raw = value.noteAccidentals?.[slot];

  if (typeof raw === "string") {
    if (raw === "") return null;
    if (raw === "n") return "♮";
    return raw;
  }

  const acc = raw?.[entryIndex];

  if (acc === "") return null;
  if (acc === "n") return "♮";
  if (acc !== undefined) return acc;

  return accidentalFromPitch(pitch);
}

function sameStaffPosition(a, b, clef) {
  return pitchToStaffIndex(a, clef) === pitchToStaffIndex(b, clef);
}

function layoutChordNotes({
  slot,
  clef,
  value,
  givenPitches = [],
  splitGivenNotes = false,
  entries = [],
  hoverPitch = null,
  offsetSingleEntrySecond = false,
  givenSymbol = "whole_note",
  hoverSymbol = "whole_note",
  givenOffsetX = 0,
  entryOffsetX = 0,
  hoverAccidental = null
}) {
  const notes = [];

  givenPitches.forEach((pitch, index) => {
    notes.push({
      id: `given-${index}`,
      pitch,
      kind: "given",
      symbol: givenSymbol,
      accidental:
        value.accidentals?.[slot] === "♮" || value.accidentals?.[slot] === ""
          ? null
          : value.accidentals?.[slot] ?? accidentalFromPitch(pitch),
      noteOffsetX: 0,
      accidentalOffsetX: undefined
    });
  });

  entries.forEach((entry, index) => {
    notes.push({
      id: `entry-${index}`,
      pitch: entry.pitch,
      kind: "entry",
      entry,
      accidental: getNoteAccidental(value, slot, index, entry.pitch),
      noteOffsetX: 0,
      accidentalOffsetX: undefined
    });
  });

  if (hoverPitch) {
    notes.push({
      id: "hover",
      pitch: hoverPitch,
      kind: "hover",
      symbol: hoverSymbol,
      accidental: hoverAccidental,
      noteOffsetX: 0,
      accidentalOffsetX: undefined
    });
  }

  const chord = notes
    .map(note => ({
      note,
      i: pitchToStaffIndex(note.pitch, clef)
    }))
    .sort((a, b) => a.i - b.i);

  // Adjacent chord tones use alternating columns so seconds do not overlap.
  // Sequential split notes retain their independent anchors.
  chord.forEach(item => {
    item.note.noteOffsetX = 0;
    item.note.accidentalOffsetX = undefined;
  });

  if (offsetSingleEntrySecond && givenPitches.length === 1) {
    // Construction against a fixed printed note: displace only the added
    // note. Applying the generic chord rule too moves both notes apart.
    const dynamicNote = notes.find(note => note.kind === "entry" || note.kind === "hover");
    if (dynamicNote && needsSecondOffset(dynamicNote.pitch, givenPitches[0], clef)) {
      dynamicNote.noteOffsetX = secondOffsetDirection(dynamicNote.pitch, givenPitches[0], clef);
    }
  } else if (!splitGivenNotes) {
    for (let i = 1; i < chord.length; i++) {
      if (chord[i].i - chord[i - 1].i === 1) {
        chord[i].note.noteOffsetX = chord[i - 1].note.noteOffsetX === 0 ? 13 : 0;
      }
    }
  }

  notes.forEach(note => {
    note.renderX = (note.noteOffsetX || 0) +
      (note.kind === "given" ? givenOffsetX : entryOffsetX);
  });
  // A split pair represents sequential notes, not one harmonic chord.
  // Keep each printed note's accidental next to its own horizontal anchor.
  if (splitGivenNotes) {
    notes.filter(note => note.kind === "given").forEach(note =>
      arrangeChordAccidentals([note], clef));
    arrangeChordAccidentals(notes.filter(note => note.kind !== "given"), clef);
  } else {
    arrangeChordAccidentals(notes, clef);
  }

  return Object.fromEntries(notes.map(note => [note.id, note]));
}
